import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  LayoutDashboard,
  BookOpen,
  Calendar,
  Calculator,
  LogOut,
  Menu,
  X,
  User as UserIcon,
  GraduationCap,
  Package,
  Truck,
  Users,
  Bell,
  Building2,
  Shield,
  ChevronDown,
  ClipboardList,
  FileText,
  Settings,
  ListChecks
} from 'lucide-react';
import {
  isNavigationItemActive,
  resolveNavigationItems,
} from './navigation';
import './Layout.css';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const { items: notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const { canDisplay } = usePermissions();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const menuTriggerRefs = useRef({});
  const menuItemRefs = useRef({});
  const pendingMenuFocusRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0];

  const topNotifications = useMemo(() => notifications.slice(0, 8), [notifications]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigationDefinitions = useMemo(() => {
    const dashboard = {
      path: '/',
      label: t('nav.dashboard'),
      icon: LayoutDashboard,
      alwaysShow: true,
    };
    const formations = {
      path: '/formations',
      label: t('nav.formations'),
      icon: BookOpen,
      resource: 'formations',
    };
    const sessions = {
      path: '/sessions',
      label: t('nav.sessions'),
      icon: Calendar,
      resource: 'sessions',
    };
    const actionItems = {
      path: '/action-items',
      label: t('nav.actionItems'),
      icon: ListChecks,
      resource: 'action_items',
    };
    const accounting = {
      path: '/comptabilite',
      label: t('nav.accounting'),
      icon: Calculator,
      resource: 'comptabilite',
    };
    const centres = {
      path: '/centres',
      label: t('nav.centres'),
      icon: Building2,
      resource: 'centres',
    };
    const consumables = {
      path: '/consommables',
      label: t('nav.consommables'),
      icon: Package,
      resource: 'consommables',
    };
    const instructors = {
      path: '/instructors',
      label: t('nav.instructors'),
      icon: GraduationCap,
      resource: 'instructors',
    };
    const requests = {
      path: '/mes-demandes',
      label: t('nav.demandes'),
      icon: ClipboardList,
      resource: 'demandes',
    };
    const supplierRequests = {
      path: '/demandes-fournisseurs',
      label: t('nav.supplierRequests'),
      icon: Truck,
      resource: 'supplier_requests',
    };
    const suppliers = {
      path: '/fournisseurs',
      label: t('nav.fournisseurs'),
      icon: Truck,
      resource: 'fournisseurs',
    };
    const administration = {
      key: 'administration',
      label: t('nav.administration'),
      icon: Settings,
      adminOnly: true,
      children: [
        { path: '/users', label: t('nav.users'), icon: Users },
        {
          path: '/permissions',
          label: t('nav.permissions'),
          icon: Shield,
        },
        { path: '/reporting-api', label: t('nav.reportingApi', 'API Reporting'), icon: Shield },
        { path: '/logs', label: t('nav.logs'), icon: FileText },
      ],
    };

    return {
      desktop: [
        dashboard,
        formations,
        sessions,
        {
          key: 'follow-up',
          label: t('nav.followUp'),
          icon: ListChecks,
          children: [actionItems, requests],
        },
        {
          key: 'structure',
          label: t('nav.structure'),
          icon: Building2,
          children: [centres, instructors],
        },
        {
          key: 'purchasing-stock',
          label: t('nav.purchasingStock'),
          icon: Package,
          children: [consumables, supplierRequests, suppliers],
        },
        accounting,
        administration,
      ],
      mobile: [
        dashboard,
        formations,
        sessions,
        actionItems,
        accounting,
        centres,
        consumables,
        instructors,
        requests,
        supplierRequests,
        suppliers,
        administration,
      ],
    };
  }, [t]);

  const desktopNavItems = useMemo(() => (
    resolveNavigationItems(
      navigationDefinitions.desktop,
      { canDisplay, isAdmin },
    )
  ), [navigationDefinitions, canDisplay, isAdmin]);

  const mobileNavItems = useMemo(() => (
    resolveNavigationItems(
      navigationDefinitions.mobile,
      { canDisplay, isAdmin },
    )
  ), [navigationDefinitions, canDisplay, isAdmin]);

  const focusMenuItem = useCallback((menuKey, targetIndex) => {
    const menuItems = (menuItemRefs.current[menuKey] || []).filter(Boolean);
    if (menuItems.length === 0) return;

    const normalizedIndex = (
      (targetIndex % menuItems.length) + menuItems.length
    ) % menuItems.length;
    menuItems[normalizedIndex].focus();
  }, []);

  const openNavigationMenu = (menuKey, targetIndex = 0) => {
    pendingMenuFocusRef.current = { menuKey, targetIndex };

    if (openMenuKey === menuKey) {
      focusMenuItem(menuKey, targetIndex);
      pendingMenuFocusRef.current = null;
      return;
    }

    setOpenMenuKey(menuKey);
  };

  const handleMenuItemKeyDown = (event, menuKey, itemIndex) => {
    const menuItems = (menuItemRefs.current[menuKey] || []).filter(Boolean);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusMenuItem(menuKey, itemIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusMenuItem(menuKey, itemIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusMenuItem(menuKey, 0);
        break;
      case 'End':
        event.preventDefault();
        focusMenuItem(menuKey, menuItems.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        pendingMenuFocusRef.current = null;
        setOpenMenuKey(null);
        menuTriggerRefs.current[menuKey]?.focus();
        break;
      case 'Tab':
        pendingMenuFocusRef.current = null;
        setOpenMenuKey(null);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const pendingFocus = pendingMenuFocusRef.current;
    if (!pendingFocus || pendingFocus.menuKey !== openMenuKey) return;

    focusMenuItem(pendingFocus.menuKey, pendingFocus.targetIndex);
    pendingMenuFocusRef.current = null;
  }, [focusMenuItem, openMenuKey]);

  useEffect(() => {
    if (!openMenuKey) return;

    const menuStillAvailable = [
      ['desktop', desktopNavItems],
      ['mobile', mobileNavItems],
    ].some(([navigationId, items]) => items.some((item) => (
      item.children && openMenuKey === `${navigationId}:${item.key}`
    )));

    if (!menuStillAvailable) {
      pendingMenuFocusRef.current = null;
      setOpenMenuKey(null);
    }
  }, [desktopNavItems, mobileNavItems, openMenuKey]);

  const renderNavigationItems = (items, navigationId) => items.map((item) => {
    const Icon = item.icon;
    const active = isNavigationItemActive(item, location.pathname);

    if (!item.children) {
      return (
        <Link
          key={`${navigationId}:${item.path}`}
          to={item.path}
          className={`nav-link ${active ? 'nav-link-active' : ''}`}
          aria-current={active ? 'page' : undefined}
          onClick={() => {
            setOpenMenuKey(null);
            setMenuOpen(false);
          }}
        >
          <span className="nav-link-icon" aria-hidden="true">
            <Icon size={18} />
          </span>
          <span className="nav-link-label">{item.label}</span>
        </Link>
      );
    }

    const scopedMenuKey = `${navigationId}:${item.key}`;
    const open = openMenuKey === scopedMenuKey;
    const menuId = `${navigationId}-nav-menu-${item.key}`;
    const triggerId = `${navigationId}-nav-menu-trigger-${item.key}`;

    return (
      <div key={scopedMenuKey} className="nav-item-dropdown">
        <button
          id={triggerId}
          ref={(element) => {
            menuTriggerRefs.current[scopedMenuKey] = element;
          }}
          className={`nav-link nav-link-dropdown ${active ? 'nav-link-active' : ''}`}
          onClick={() => {
            if (open) {
              pendingMenuFocusRef.current = null;
              setOpenMenuKey(null);
              return;
            }

            openNavigationMenu(scopedMenuKey);
          }}
          onKeyDown={(event) => {
            if (
              event.key === 'ArrowDown'
              || event.key === 'Enter'
              || event.key === ' '
            ) {
              event.preventDefault();
              openNavigationMenu(scopedMenuKey);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              openNavigationMenu(
                scopedMenuKey,
                item.children.length - 1,
              );
            } else if (event.key === 'Escape' && open) {
              event.preventDefault();
              pendingMenuFocusRef.current = null;
              setOpenMenuKey(null);
            }
          }}
          type="button"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={menuId}
        >
          <span className="nav-link-icon" aria-hidden="true">
            <Icon size={18} />
          </span>
          <span className="nav-link-label">{item.label}</span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`dropdown-icon ${open ? 'dropdown-icon-open' : ''}`}
          />
        </button>

        {open && (
          <div
            id={menuId}
            className="dropdown-menu"
            role="menu"
            aria-labelledby={triggerId}
          >
            {item.children.map((childItem, childIndex) => {
              const ChildIcon = childItem.icon;
              const childActive = isNavigationItemActive(
                childItem,
                location.pathname,
              );

              return (
                <Link
                  key={`${scopedMenuKey}:${childItem.path}`}
                  to={childItem.path}
                  role="menuitem"
                  tabIndex={-1}
                  ref={(element) => {
                    if (!menuItemRefs.current[scopedMenuKey]) {
                      menuItemRefs.current[scopedMenuKey] = [];
                    }
                    menuItemRefs.current[scopedMenuKey][childIndex] = element;
                  }}
                  aria-current={childActive ? 'page' : undefined}
                  className={`dropdown-item ${childActive ? 'dropdown-item-active' : ''}`}
                  onClick={() => {
                    pendingMenuFocusRef.current = null;
                    setOpenMenuKey(null);
                    setMenuOpen(false);
                  }}
                  onKeyDown={(event) => {
                    handleMenuItemKeyDown(
                      event,
                      scopedMenuKey,
                      childIndex,
                    );
                  }}
                >
                  <ChildIcon size={16} aria-hidden="true" />
                  <span>{childItem.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  });

  const getInitials = () => {
    if (!user) return '??';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const getUserRoleLabel = () => {
    const role = user?.role;
    if (role === 'ADMIN') return t('roles.admin');
    if (role === 'BU_COORDINATOR') return t('roles.bu_coordinator');
    if (role === 'MANAGER') return t('roles.manager');
    if (role === 'COMPTABLE') return t('roles.comptable');
    if (role === 'LOGISTIQUE_COORDINATOR') return t('roles.logistique_coordinator');
    if (role === 'FOURNISSEUR') return t('roles.fournisseur') || 'Fournisseur';
    if (role === 'RESPONSABLE') return t('roles.bu_coordinator');
    return t('roles.student');
  };

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="menu-toggle"
              onClick={() => {
                setMenuOpen(!menuOpen);
                setNotifOpen(false);
                setUserMenuOpen(false);
              }}
              aria-label="Toggle menu"
              aria-controls="mobile-navigation"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <Link to="/" className="logo-link">
              <img src="/logo.png" alt="PMS (Procurement Management System)" className="header-logo" />
            </Link>

            <nav
              className="nav nav-desktop"
              aria-label={t('nav.main')}
            >
              {renderNavigationItems(desktopNavItems, 'desktop')}
            </nav>

            <nav
              id="mobile-navigation"
              className={`nav nav-mobile ${menuOpen ? 'nav-open' : ''}`}
              aria-label={t('nav.mobile')}
              hidden={!menuOpen}
            >
              {renderNavigationItems(mobileNavItems, 'mobile')}
            </nav>
          </div>

          <div className="header-right">
            <select
              value={currentLang}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              aria-label={t('language.label')}
              className="lang-select"
            >
              <option value="fr">{t('language.fr')}</option>
              <option value="en">{t('language.en')}</option>
              <option value="pt">{t('language.pt')}</option>
            </select>

            <div className="notif-wrapper">
              <button
                className="notif-btn"
                onClick={() => {
                  setNotifOpen((v) => !v);
                  setUserMenuOpen(false);
                  setMenuOpen(false);
                }}
                title={t('common.notifications')}
                type="button"
              >
                <Bell size={18} />
                {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
              </button>

              {notifOpen && (
                <div className="notif-dropdown">
                  <div className="notif-dropdown-header">
                    <span className="notif-title">{t('common.notifications')}</span>
                    <button className="notif-markall" type="button" onClick={async () => { await markAllRead(); }}>
                      {t('common.markAllRead')}
                    </button>
                  </div>
                  <div className="notif-list">
                    {topNotifications.length === 0 ? (
                      <div className="notif-empty">{t('common.noNotifications')}</div>
                    ) : (
                      topNotifications.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          className={`notif-item ${n.is_read ? '' : 'notif-item-unread'}`}
                          onClick={async () => {
                            if (!n.is_read) await markRead(n.id);
                            setNotifOpen(false);
                            if (n?.data?.action_item_id) navigate(`/action-items/${n.data.action_item_id}`);
                            else if (n?.data?.order_id) navigate(`/mes-demandes/${n.data.order_id}`);
                            else if (n?.data?.session_id) navigate(`/sessions/${n.data.session_id}`);
                            else if (n?.data?.formation_id) navigate(`/formations/${n.data.formation_id}`);
                          }}
                        >
                          <div className="notif-item-title">{n.title}</div>
                          <div className="notif-item-msg">{n.message}</div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="user-menu-wrapper">
              <button
                className="user-profile"
                onClick={() => {
                  setUserMenuOpen(!userMenuOpen);
                  setNotifOpen(false);
                  setMenuOpen(false);
                }}
                type="button"
              >
                <div className="user-avatar">
                  {getInitials() || <UserIcon size={16} />}
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.first_name} {user?.last_name}</span>
                  <span className="user-role">
                    {getUserRoleLabel()}
                  </span>
                </div>
                <ChevronDown size={16} className="user-menu-icon" />
              </button>

              {userMenuOpen && (
                <div className="user-dropdown">
                  <button
                    className="user-dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/profile');
                    }}
                    type="button"
                  >
                    <UserIcon size={16} />
                    <span>{t('common.profile') || 'Profil'}</span>
                  </button>
                  <button
                    className="user-dropdown-item user-dropdown-logout"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setMenuOpen(false);
                      handleLogout();
                    }}
                    type="button"
                  >
                    <LogOut size={16} />
                    <span>{t('common.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;

